import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { t } from '../i18n';
import { Combobox } from './Combobox';

/** `[[Bild.png]]` → `Bild.png`. */
export function imageTarget(v: unknown): string {
  const s = typeof v === 'string' ? v : '';
  const m = /^\s*!?\[\[([^\]|#]+)(?:[|#][^\]]*)?\]\]\s*$/.exec(s);
  return (m ? m[1]! : s).trim();
}

/**
 * Bildauswahl aus den Dateien der Kampagne oder per Upload.
 * Liefert den Wert für das Frontmatter (`[[Name]]`, bei doppelten Namen mit Ordner).
 */
export function ImagePicker(props: { cid: string; value: unknown; onChange: (value: string) => void; autoFocus?: boolean }) {
  const qc = useQueryClient();
  const files = useQuery({ queryKey: ['files', props.cid], queryFn: () => api.files(props.cid), staleTime: 30_000 });
  const [text, setText] = useState(imageTarget(props.value));
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const images = useMemo(() => (files.data ?? []).filter((f) => f.mime.startsWith('image/')), [files.data]);
  const options = useMemo(() => {
    const count = new Map<string, number>();
    for (const f of images) count.set(f.name.toLowerCase(), (count.get(f.name.toLowerCase()) ?? 0) + 1);
    return images.map((f) => {
      const target = (count.get(f.name.toLowerCase()) ?? 0) > 1 && f.folder ? `${f.folder}/${f.name}` : f.name;
      return { key: f.id, title: target, hint: f.folder };
    });
  }, [images]);

  const choose = (target: string) => {
    setText(target);
    props.onChange(target ? `[[${target}]]` : '');
  };

  return (
    <span className="image-picker">
      <Combobox
        value={text}
        onChange={setText}
        options={options}
        autoFocus={props.autoFocus}
        placeholder={t('map.pickImage')}
        onPick={(o) => choose(o.title)}
        onEnter={() => choose(text.trim())}
        onBlur={() => text.trim() !== imageTarget(props.value) && choose(text.trim())}
      />
      <button type="button" onClick={() => fileRef.current?.click()}>
        ⇪ {t('map.upload')}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setError(null);
          try {
            const res = await api.uploadFile(props.cid, file);
            await qc.invalidateQueries({ queryKey: ['files', props.cid] });
            choose(res.name);
          } catch (err) {
            setError(err instanceof ApiError ? err.message : String(err));
          }
          e.target.value = '';
        }}
      />
      {error && <span className="error small">{error}</span>}
    </span>
  );
}
